import { Toast as ToastPrimitive, type ToastManagerAddOptions } from '@base-ui/react/toast';
import { CircleCheckIcon, InfoIcon, Loader2Icon, OctagonXIcon, TriangleAlertIcon, XIcon } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

const toastManager = ToastPrimitive.createToastManager();

type ToastType = 'success' | 'info' | 'warning' | 'error' | 'loading';

type ToastOptions = Omit<ToastManagerAddOptions<object>, 'title' | 'type'>;

const TOAST_ICONS: Record<ToastType, React.ReactElement> = {
	success: <CircleCheckIcon />,
	info: <InfoIcon />,
	warning: <TriangleAlertIcon />,
	error: <OctagonXIcon />,
	loading: <Loader2Icon className="animate-spin" />,
};

function isToastType(type: string | undefined): type is ToastType {
	return type !== undefined && type in TOAST_ICONS;
}

function createTypedToast(type: ToastType) {
	return (title: React.ReactNode, options?: ToastOptions): string => toastManager.add({ ...options, title, type });
}

// Global manager: usable outside React (route loaders, mutation callbacks). Exposes `add`, `close`, `update`, `promise`
// plus typed shortcuts, e.g. `toast.success(t('Saved'))`.
const toast = Object.assign(toastManager, {
	success: createTypedToast('success'),
	info: createTypedToast('info'),
	warning: createTypedToast('warning'),
	error: createTypedToast('error'),
	loading: createTypedToast('loading'),
});

function Toaster({ timeout = 4000, ...props }: Omit<ToastPrimitive.Provider.Props, 'toastManager'>) {
	return (
		<ToastPrimitive.Provider toastManager={toastManager} timeout={timeout} {...props}>
			<ToastPrimitive.Portal>
				<ToastPrimitive.Viewport
					data-slot="toast-viewport"
					className="fixed top-4 right-4 z-[100] w-[calc(100vw-2rem)] sm:top-8 sm:right-8 sm:w-[22.5rem]">
					<ToastList />
				</ToastPrimitive.Viewport>
			</ToastPrimitive.Portal>
		</ToastPrimitive.Provider>
	);
}

function ToastList() {
	const { toasts } = ToastPrimitive.useToastManager();

	return toasts.map((item) => <ToastItem key={item.id} toast={item} />);
}

// Top-anchored stack: the newest toast sits at the top, older ones peek out below it and expand on hover/focus
function ToastItem({ toast: item }: { toast: ToastPrimitive.Root.ToastObject }) {
	const { t } = useTranslation();
	const icon = isToastType(item.type) ? TOAST_ICONS[item.type] : null;

	return (
		<ToastPrimitive.Root
			data-slot="toast"
			toast={item}
			swipeDirection={['up', 'right']}
			className={cn(
				'[--gap:0.75rem] [--height:var(--toast-frontmost-height,var(--toast-height))] [--offset-y:calc(var(--toast-offset-y)+var(--toast-index)*var(--gap)+var(--toast-swipe-movement-y))] [--peek:0.75rem] [--scale:calc(max(0,1-(var(--toast-index)*0.1)))] [--shrink:calc(1-var(--scale))]',
				'absolute top-0 right-0 z-[calc(1000-var(--toast-index))] h-(--height) w-full origin-top rounded-md border bg-popover text-popover-foreground shadow-lg select-none',
				'[transform:translateX(var(--toast-swipe-movement-x))_translateY(calc(var(--toast-swipe-movement-y)+var(--toast-index)*var(--peek)+var(--shrink)*var(--height)))_scale(var(--scale))]',
				'[transition:transform_0.5s_cubic-bezier(0.22,1,0.36,1),opacity_0.5s,height_0.15s]',
				// Keeps the stack expanded while the pointer crosses the gap between toasts
				"after:absolute after:top-full after:left-0 after:h-[calc(var(--gap)+1px)] after:w-full after:content-['']",
				'data-expanded:h-(--toast-height) data-expanded:[transform:translateX(var(--toast-swipe-movement-x))_translateY(var(--offset-y))]',
				'data-ending-style:opacity-0 data-limited:opacity-0 data-starting-style:[transform:translateY(-150%)]',
				'[&[data-ending-style]:not([data-limited]):not([data-swipe-direction])]:[transform:translateY(-150%)]',
				'data-ending-style:data-[swipe-direction=up]:[transform:translateY(calc(var(--toast-swipe-movement-y)-150%))]',
				'data-ending-style:data-[swipe-direction=right]:[transform:translateX(calc(var(--toast-swipe-movement-x)+150%))_translateY(var(--offset-y))]',
			)}>
			<ToastPrimitive.Content className="flex h-full items-center gap-2.5 overflow-hidden p-4 text-sm transition-opacity duration-250 data-behind:opacity-0 data-expanded:opacity-100 [&_svg]:size-4 [&_svg]:shrink-0">
				{icon}
				<div className="flex min-w-0 flex-1 flex-col gap-0.5">
					<ToastPrimitive.Title data-slot="toast-title" className="font-medium" />
					{item.description ? (
						<ToastPrimitive.Description data-slot="toast-description" className="text-muted-foreground" />
					) : null}
				</div>
				<ToastPrimitive.Close
					data-slot="toast-close"
					aria-label={t('Ui.Close')}
					className="rounded-xs opacity-70 transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden">
					<XIcon />
				</ToastPrimitive.Close>
			</ToastPrimitive.Content>
		</ToastPrimitive.Root>
	);
}

export { toast, Toaster };
